// SPDX-License-Identifier: MIT
/**
 * Turns (reel, t) into a 2D frame: picks the active scene(s), applies transitions, beat accents and
 * camera shake. Everything here is a pure function of t.
 */
import type { Reel, SceneEntry } from '@pulsereel/schema';
import { type ResolvedBrand, rgba } from './brand.ts';
import { inOutCubic, outExpo } from './core/ease.ts';
import { clamp, smoothstep } from './core/math.ts';
import { noise1 } from './core/noise.ts';
import { createRng } from './core/rng.ts';
import type { TextProbe } from './qa.ts';
import { DESIGN_H, DESIGN_W, type QAReporter, type SceneClaim, type SceneEnv, type SceneModule, type SceneSoundEvent, } from './scene.ts';
import type { Style } from './style.ts';
/** The crisp layer of one subframe. `acquire` clears it on first use and returns its context. */
export type CrispLayer = {
    acquire(): CanvasRenderingContext2D;
};
/**
 * Per-frame look. Cue accents and the active scene's `post()` override both feed it; the post
 * pipeline turns it into uniforms. Multipliers default to 1, additive terms to 0.
 */
export type FrameFx = {
    flash: number;
    aberration: number;
    shake: number;
    bloom: number;
    exposure: number;
    contrast: number;
    saturation: number;
    tint: [
        number,
        number,
        number
    ];
    vignette: number;
    grain: number;
    /** Scale of the style's base lens effects (aberration, distortion). */
    lens: number;
};
export const NEUTRAL_FX: FrameFx = {
    flash: 0,
    aberration: 0,
    shake: 0,
    bloom: 1,
    exposure: 1,
    contrast: 1,
    saturation: 1,
    tint: [1, 1, 1],
    vignette: 1,
    grain: 1,
    lens: 1,
};
export type CompositorOptions = {
    reel: Reel;
    brand: ResolvedBrand;
    style: Style;
    scenes: Record<string, SceneModule<never>>;
    size: [
        number,
        number
    ];
};
const TRANSITION_BEATS: Record<string, number> = { iris: 0.75, bars: 0.5, flash: 0.25, 'zoom-through': 0.5 };
export class Compositor {
    readonly spb: number;
    readonly duration: number;
    private readonly opts: CompositorOptions;
    private readonly parsedProps: Map<string, unknown>;
    constructor(opts: CompositorOptions) {
        this.opts = opts;
        this.spb = 60 / opts.reel.bpm;
        this.duration = opts.reel.beats * this.spb;
        this.parsedProps = new Map();
        for (const entry of opts.reel.scenes) {
            const mod = this.module(entry);
            const parsed = mod.propsSchema.safeParse(entry.props);
            if (!parsed.success)
                throw new Error(`scene ${entry.id} (${entry.type}): invalid props — ${parsed.error.message}`);
            this.parsedProps.set(entry.id, parsed.data);
        }
    }
    private module(entry: SceneEntry): SceneModule<never> {
        const mod = this.opts.scenes[entry.type];
        if (!mod)
            throw new Error(`scene ${entry.id}: unknown scene type "${entry.type}"`);
        return mod;
    }
    /** Scene sound requests converted to global beats (clipped to each scene's range). */
    soundEvents(): SceneSoundEvent[] {
        return this.opts.reel.scenes.flatMap((e) => {
            const timing = { beats: e.beats[1] - e.beats[0], spb: this.spb };
            const events = this.module(e).audioEvents?.(this.parsedProps.get(e.id) as never, timing) ?? [];
            return events
                .map((ev) => ({ ...ev, beat: e.beats[0] + ev.beat }))
                .filter((ev) => ev.beat >= e.beats[0] && ev.beat < e.beats[1]);
        });
    }
    /** What the scenes claim about the product, for the QA claims check. */
    claims(): SceneClaim[] {
        return this.opts.reel.scenes.flatMap((e) => (this.module(e).claims?.(this.parsedProps.get(e.id) as never) ?? []).map((text) => ({
            scene: e.id,
            text,
        })));
    }
    /** All text the reel can draw, for font preloading. */
    glyphs(): string {
        return this.opts.reel.scenes
            .map((e) => this.module(e).glyphs?.(this.parsedProps.get(e.id) as never) ?? '')
            .join('');
    }
    private env(entry: SceneEntry, t: number, report?: QAReporter, crisp: SceneEnv['crisp'] = (draw) => draw(this.scratch())): SceneEnv {
        const { reel, brand, style, size } = this.opts;
        const start = entry.beats[0] * this.spb;
        const env: SceneEnv = {
            t,
            local: t - start,
            beat: t / this.spb,
            localBeat: t / this.spb - entry.beats[0],
            dur: (entry.beats[1] - entry.beats[0]) * this.spb,
            spb: this.spb,
            size,
            brand,
            style: this.styleOf(entry, style),
            rng: createRng(reel.seed, entry.id),
            crisp,
        };
        if (report)
            env.report = report;
        return env;
    }
    /** The style as the scene sees it: the reel's, with the scene's own ground if it sets one. */
    private styleOf(entry: SceneEntry, style: Style): Style {
        if (!entry.ground || entry.ground === style.background)
            return style;
        let s = this.grounded.get(entry.ground);
        if (!s)
            this.grounded.set(entry.ground, (s = { ...style, background: entry.ground }));
        return s;
    }
    private readonly grounded = new Map<Style['background'], Style>();
    private drawScene(ctx: CanvasRenderingContext2D, entry: SceneEntry, t: number, probe?: TextProbe, overlay?: CrispLayer) {
        ctx.save();
        probe?.enterScene(entry.id);
        const report = probe?.reporter(ctx, entry.id);
        const crisp: SceneEnv['crisp'] = (draw) => {
            const target = overlay?.acquire();
            const c = target ?? ctx;
            c.save();
            if (target) {
                target.setTransform(ctx.getTransform());
                target.globalAlpha = ctx.globalAlpha;
            }
            draw(c);
            c.restore();
        };
        this.module(entry).draw(ctx, this.parsedProps.get(entry.id) as never, this.env(entry, t, report, crisp));
        ctx.restore();
    }
    /** Off-frame env (post overrides, sound events): crisp draws are dropped. */
    private scratch(): CanvasRenderingContext2D {
        this.nullCtx ??= new OffscreenCanvas(1, 1).getContext('2d') as unknown as CanvasRenderingContext2D;
        return this.nullCtx;
    }
    private nullCtx: CanvasRenderingContext2D | undefined;
    /** Beat accents from cues, decaying after each hit. */
    fx(t: number): FrameFx {
        const beat = t / this.spb;
        let flash = 0;
        let aberration = 0;
        let shake = 0;
        for (const cue of this.opts.reel.cues) {
            const db = beat - cue.beat;
            if (db < 0 || db > 2)
                continue;
            const a = cue.accent ?? (cue.sfx ? 0.6 : 0);
            flash = Math.max(flash, a * 0.12 * Math.exp(-db * 14));
            aberration = Math.max(aberration, a * Math.exp(-db * 5));
            shake = Math.max(shake, a * Math.exp(-db * 7));
        }
        // Scene "flash" transitions peak on the boundary.
        // A flash cut is a hot frame, not a fade: a very fast rise into the cut, a quick decay after.
        for (const entry of this.opts.reel.scenes) {
            if (entry.out !== 'flash')
                continue;
            const d = beat - entry.beats[1];
            if (d > -0.08 && d < 0)
                flash = Math.max(flash, 0.85 * (1 + d / 0.08) ** 2);
            else if (d >= 0 && d < 0.5)
                flash = Math.max(flash, 0.85 * Math.exp(-d * 16));
        }
        const fx: FrameFx = { ...NEUTRAL_FX, flash, aberration, shake };
        // The active scene may shape its own look (exposure ramps, grades, bloom swells, extra hits).
        const entry = this.sceneAt(beat);
        const mod = this.module(entry);
        const over = mod.post?.(this.parsedProps.get(entry.id) as never, this.env(entry, t));
        if (over) {
            fx.flash = Math.max(fx.flash, over.flash ?? 0);
            fx.aberration = Math.max(fx.aberration, over.aberration ?? 0);
            fx.shake = Math.max(fx.shake, over.shake ?? 0);
            fx.bloom = over.bloom ?? fx.bloom;
            fx.exposure = over.exposure ?? fx.exposure;
            fx.contrast = over.contrast ?? fx.contrast;
            fx.saturation = over.saturation ?? fx.saturation;
            fx.tint = over.tint ?? fx.tint;
            fx.vignette = over.vignette ?? fx.vignette;
            fx.grain = over.grain ?? fx.grain;
            fx.lens = clamp(over.lens ?? fx.lens);
        }
        fx.flash = clamp(fx.flash);
        return fx;
    }
    private sceneAt(beat: number): SceneEntry {
        const b = clamp(beat, 0, this.opts.reel.beats - 1e-6);
        return this.opts.reel.scenes.find((s) => b >= s.beats[0] && b < s.beats[1]) ?? this.opts.reel.scenes[0]!;
    }
    /** Draw the frame at `t`. With a QA probe, every scene drawn (both sides of a transition) is captured. */
    draw(ctx: CanvasRenderingContext2D, t: number, probe?: TextProbe, overlay?: CrispLayer): void {
        const { reel, brand, size } = this.opts;
        const beat = clamp(t / this.spb, 0, reel.beats - 1e-6);
        const i = reel.scenes.findIndex((s) => beat >= s.beats[0] && beat < s.beats[1]);
        const current = reel.scenes[Math.max(0, i)]!;
        const prev = i > 0 ? reel.scenes[i - 1] : undefined;
        // Start every subframe from a blank bitmap and default state, so nothing from the previous
        // frame (uncovered edges under camera shake, translucent transitions, leaked ctx state) can leak in.
        ctx.reset();
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, size[0], size[1]);
        ctx.setTransform(size[0] / DESIGN_W, 0, 0, size[1] / DESIGN_H, 0, 0);
        const shake = this.fx(t).shake;
        if (shake > 0.001) {
            // Punch in just enough that the shaken frame still covers the canvas: no black slivers at the
            // edges, which show on light grounds.
            const s = 1 + Math.max(28 / DESIGN_W, 20 / DESIGN_H) * shake;
            ctx.translate(DESIGN_W / 2 + noise1(t * 24, 11) * 14 * shake, DESIGN_H / 2 + noise1(t * 24, 29) * 10 * shake);
            ctx.scale(s, s);
            ctx.translate(-DESIGN_W / 2, -DESIGN_H / 2);
        }
        // Transitions run over the first part of the incoming scene, with the outgoing scene continuing.
        const kind = prev?.out;
        const span = kind ? (TRANSITION_BEATS[kind] ?? 0) : 0;
        const into = beat - current.beats[0];
        if (!prev || !kind || into >= span || kind === 'flash') {
            // Only outside transitions: inside one, wipes and irises drawn into the frame must cover text.
            this.drawScene(ctx, current, t, probe, overlay);
        }
        else {
            const k = into / span;
            if (kind === 'iris') {
                this.drawScene(ctx, prev, t, probe);
                const radius = outExpo(k) * Math.hypot(DESIGN_W, DESIGN_H) * 0.6;
                ctx.save();
                ctx.beginPath();
                ctx.arc(DESIGN_W / 2, DESIGN_H / 2, radius, 0, Math.PI * 2);
                ctx.clip();
                this.drawScene(ctx, current, t, probe);
                ctx.restore();
                ctx.strokeStyle = rgba(brand.colors.accent, 1 - k);
                ctx.lineWidth = 6;
                ctx.beginPath();
                ctx.arc(DESIGN_W / 2, DESIGN_H / 2, radius, 0, Math.PI * 2);
                ctx.stroke();
            }
            else if (kind === 'bars') {
                // Bars drop in over the outgoing scene, then fall away to reveal the incoming one.
                const covering = k < 0.5;
                this.drawScene(ctx, covering ? prev : current, t, probe);
                const n = 6;
                const w = DESIGN_W / n;
                for (let b = 0; b < n; b++) {
                    const kk = smoothstep(b * 0.08, 0.6 + b * 0.08, covering ? k * 2 : (k - 0.5) * 2);
                    ctx.fillStyle = b % 2 ? brand.colors.primary : brand.colors.accent;
                    if (covering)
                        ctx.fillRect(b * w, 0, w + 1, DESIGN_H * kk);
                    else
                        ctx.fillRect(b * w, DESIGN_H * kk, w + 1, DESIGN_H * (1 - kk));
                }
            }
            else {
                // zoom-through
                const e = inOutCubic(k);
                ctx.save();
                ctx.globalAlpha = 1 - e;
                ctx.translate(DESIGN_W / 2, DESIGN_H / 2);
                ctx.scale(1 + e * 0.6, 1 + e * 0.6);
                ctx.translate(-DESIGN_W / 2, -DESIGN_H / 2);
                this.drawScene(ctx, prev, t, probe);
                ctx.restore();
                ctx.save();
                ctx.globalAlpha = e;
                ctx.translate(DESIGN_W / 2, DESIGN_H / 2);
                ctx.scale(0.85 + 0.15 * e, 0.85 + 0.15 * e);
                ctx.translate(-DESIGN_W / 2, -DESIGN_H / 2);
                this.drawScene(ctx, current, t, probe);
                ctx.restore();
            }
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
}
